/**
 * Public guest page — /find/:slug
 * Landing → (guest details) → selfie → results (grid, lightbox, select & download, share).
 * No auth. Mobile-first. Works inside WhatsApp / Instagram in-app browsers.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Camera, Upload, Download, Share2, Check, X, ChevronLeft, ChevronRight, Loader2, Sparkles,
  RefreshCw, Images, AlertTriangle, ShieldCheck, CheckSquare, Square, Link as LinkIcon, MessageCircle,
  Globe, UserPlus, Phone,
} from "lucide-react";

// lucide no longer ships brand icons — small inline Instagram glyph
const Instagram = ({ className = "", style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);
import { facePublic, errMsg, abs, resizeImageFile, downloadZip, API } from "../lib/faceApi";
import axios from "axios";

const DEFAULT_ACCENT = "#f5a623";
const lsKey = (slug, k) => `face_${k}_${slug}`;

function useToast() {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, kind = "info") => {
    setToast({ msg, kind });
    window.clearTimeout(show._t);
    show._t = window.setTimeout(() => setToast(null), 3200);
  }, []);
  return [toast, show];
}

function Toast({ toast }) {
  if (!toast) return null;
  const color = toast.kind === "error" ? "bg-red-500/95" : toast.kind === "success" ? "bg-emerald-500/95" : "bg-zinc-800/95";
  return (
    <div className={`fixed left-1/2 top-4 z-[100] -translate-x-1/2 rounded-full px-4 py-2 text-sm text-white shadow-xl ${color}`}>
      {toast.msg}
    </div>
  );
}

function Button({ children, className = "", variant = "primary", ...rest }) {
  const base = "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none";
  const styles = {
    primary: "text-black shadow-lg shadow-amber-500/20",
    ghost: "bg-white/10 text-white hover:bg-white/15 border border-white/10",
    dark: "bg-zinc-900 text-white border border-white/10 hover:border-white/20",
  };
  return (
    <button className={`${base} ${styles[variant]} ${className}`} style={variant === "primary" ? { background: "var(--accent)" } : undefined} {...rest}>
      {children}
    </button>
  );
}

function brandOf(event) {
  const b = event?.brand_settings || {};
  return { ...b, display_name: b.display_name || event?.brand_label || "Witty Shadows", primary_color: b.primary_color || DEFAULT_ACCENT };
}

function Shell({ event, children, footer = true }) {
  const b = brandOf(event);
  return (
    <div className="min-h-screen bg-[#0b0b0c] text-white" style={{ fontFamily: "Inter, system-ui, -apple-system, Segoe UI, sans-serif", "--accent": b.primary_color }}>
      <div className="mx-auto flex min-h-screen w-full max-w-3xl flex-col px-4 pb-10">
        {children}
        {footer && (
          <div className="mt-auto flex flex-col items-center gap-2 pt-10 text-center text-xs text-white/40">
            {b.logo_url ? <img src={b.logo_url} alt={b.display_name} className="h-auto max-h-10 w-auto max-w-[180px] opacity-70" /> : null}
            <span>Photographed & powered by <span className="font-semibold text-white/70">{b.display_name}</span></span>
          </div>
        )}
      </div>
    </div>
  );
}

function StudioCard({ event, compact = false }) {
  const b = brandOf(event);
  if (b.show_cta === false) return null;
  const links = [
    b.website_url ? { href: b.website_url, label: "See our work", Icon: Globe } : null,
    b.instagram_url ? { href: b.instagram_url, label: "Instagram", Icon: Instagram } : null,
    b.whatsapp_url ? { href: `${b.whatsapp_url}?text=${encodeURIComponent(`Hi ${b.display_name}! I found my photos from ${event?.couple_names || event?.title || "the wedding"} and wanted to get in touch.`)}`, label: "WhatsApp us", Icon: MessageCircle } : null,
    (b.whatsapp_number || b.phone) && b.vcard_url ? { href: b.vcard_url, label: "Save our number", Icon: UserPlus, download: true } : null,
    !b.whatsapp_url && b.phone ? { href: `tel:${b.phone}`, label: "Call us", Icon: Phone } : null,
  ].filter(Boolean);
  if (!links.length) return null;
  return (
    <div className={`rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] ${compact ? "p-4" : "p-5"}`}>
      <div className="flex items-center gap-3">
        {b.logo_url ? (
          <img src={b.logo_url} alt={b.display_name} className="h-auto max-h-12 w-auto max-w-[170px] object-contain" />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-sm font-bold" style={{ color: "var(--accent)" }}>{b.display_name.slice(0, 1)}</div>
        )}
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{b.cta_title || `Loved these photos?`}</div>
          <div className="truncate text-xs text-white/60">{b.tagline || b.display_name}</div>
        </div>
      </div>
      {!compact && b.cta_text && <p className="mt-3 text-sm text-white/70">{b.cta_text}</p>}
      <div className={`mt-3 grid gap-2 ${links.length > 2 ? "grid-cols-2" : "grid-cols-1"}`}>
        {links.map(({ href, label, Icon, download }) => (
          <a key={label} href={href} download={download ? "" : undefined} target={download ? undefined : "_blank"} rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm font-medium hover:bg-white/10">
            <Icon className="h-4 w-4" style={{ color: "var(--accent)" }} /> {label}
          </a>
        ))}
      </div>
    </div>
  );
}

function Hero({ event, compact = false }) {
  const date = event?.event_date ? new Date(event.event_date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : null;
  return (
    <div className={`relative -mx-4 overflow-hidden ${compact ? "h-40" : "h-[52vh] min-h-[320px]"}`}>
      {event?.cover_url ? (
        <img src={event.cover_url} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/40 via-zinc-900 to-black" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0c] via-[#0b0b0c]/40 to-black/30" />
      <div className="absolute inset-x-0 bottom-0 px-4 pb-4">
        {brandOf(event).logo_url ? (
          <img src={brandOf(event).logo_url} alt={brandOf(event).display_name} className={`${compact ? "max-h-9 max-w-[150px]" : "max-h-16 max-w-[240px]"} mb-2 h-auto w-auto object-contain drop-shadow`} />
        ) : (
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--accent)" }}>{brandOf(event).display_name}</div>
        )}
        <h1 className={`mt-1 font-semibold leading-tight ${compact ? "text-xl" : "text-3xl"}`}>{event?.couple_names || event?.title}</h1>
        {!compact && (
          <div className="mt-1 text-sm text-white/70">{[event?.title !== event?.couple_names ? event?.title : null, date].filter(Boolean).join(" · ")}</div>
        )}
      </div>
    </div>
  );
}

function Landing({ event, onStart }) {
  const ready = event.is_ready;
  return (
    <>
      <Hero event={event} />
      <div className="mt-6 space-y-5">
        <div>
          <h2 className="text-2xl font-semibold">Find your photos in seconds</h2>
          <p className="mt-2 text-white/70">
            Take a quick selfie and we'll pick out every photo you're in from {event.photos_total > 0 ? `${event.photos_total.toLocaleString("en-IN")} wedding photos` : "the wedding photos"}.
            {event.allow_download ? " Download them in full quality." : ""}
          </p>
        </div>
        {!ready && (
          <div className="flex items-start gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm">
            <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-amber-300" />
            <div>
              <div className="font-semibold text-amber-200">{event.photos_total > 0 ? "The gallery is still being prepared" : "Photos are on their way"}</div>
              <div className="text-white/70">{event.photos_total > 0 ? `${event.photos_indexed} of ${event.photos_total} photos ready. Please check back in a little while.` : "The photographers are uploading as the celebration happens — come back to this link in a bit and take your selfie."}</div>
            </div>
          </div>
        )}
        <Button onClick={onStart} disabled={!ready} className="w-full py-4 text-base">
          <Sparkles className="h-5 w-5" /> Find my photos
        </Button>
        <div className="flex items-center justify-center gap-2 text-xs text-white/50">
          <ShieldCheck className="h-4 w-4" /> Your selfie is only used to search and is never stored.
        </div>
        <StudioCard event={event} compact />
      </div>
    </>
  );
}

function GuestForm({ event, onDone, showToast }) {
  const [name, setName] = useState(() => localStorage.getItem(lsKey(event.slug, "name")) || "");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(true);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return showToast("Please enter your name", "error");
    if (phone.replace(/\D/g, "").length < 10) return showToast("Please enter a valid WhatsApp number", "error");
    if (event.requires_pin && !pin) return showToast("Please enter the gallery PIN", "error");
    setBusy(true);
    try {
      const res = await facePublic.guest(event.slug, { name: name.trim(), phone: phone.trim(), consent, pin: pin || null });
      try {
        localStorage.setItem(lsKey(event.slug, "guest"), res.guest_id);
        localStorage.setItem(lsKey(event.slug, "name"), res.name || name.trim());
        if (pin) sessionStorage.setItem(lsKey(event.slug, "pin"), pin);
      } catch (_) { /* private mode */ }
      onDone(res.guest_id, pin);
    } catch (err) {
      showToast(errMsg(err), "error");
    } finally {
      setBusy(false);
    }
  };
  const input = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white placeholder-white/30 outline-none focus:border-amber-400/60";
  return (
    <>
      <Hero event={event} compact />
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <h2 className="text-xl font-semibold">Tell us who you are</h2>
          <p className="mt-1 text-sm text-white/60">So we can send you your photos and keep them for you.</p>
        </div>
        <label className="block space-y-1.5">
          <span className="text-xs font-medium uppercase tracking-wider text-white/50">Your name</span>
          <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Priya Sharma" autoComplete="name" />
        </label>
        <label className="block space-y-1.5">
          <span className="text-xs font-medium uppercase tracking-wider text-white/50">WhatsApp number</span>
          <div className="flex gap-2">
            <span className="flex items-center rounded-xl border border-white/10 bg-white/5 px-3 text-white/70">+91</span>
            <input className={input} inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="98765 43210" autoComplete="tel" />
          </div>
        </label>
        {event.requires_pin && (
          <label className="block space-y-1.5">
            <span className="text-xs font-medium uppercase tracking-wider text-white/50">Gallery PIN</span>
            <input className={input} inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="From your invite" />
          </label>
        )}
        <label className="flex cursor-pointer items-start gap-3 text-sm text-white/70">
          <button type="button" onClick={() => setConsent(!consent)} className="mt-0.5 text-amber-400">
            {consent ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5" />}
          </button>
          <span>Send my photos & studio updates on WhatsApp</span>
        </label>
        <Button type="submit" disabled={busy} className="w-full py-4 text-base">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />} Continue to selfie
        </Button>
      </form>
    </>
  );
}

function SelfieStep({ event, guestId, pin, onResults, onBack, showToast }) {
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    setPreview(URL.createObjectURL(file));
    try {
      const blob = await resizeImageFile(file, 1200);
      const res = await facePublic.search(event.slug, blob, guestId, pin);
      try { localStorage.setItem(lsKey(event.slug, "result"), res.token); } catch (_) { /* ignore */ }
      onResults(res);
    } catch (err) {
      showToast(errMsg(err), "error");
      setPreview(null);
    } finally {
      setBusy(false);
      if (camRef.current) camRef.current.value = "";
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <>
      <Hero event={event} compact />
      <div className="mt-6 space-y-5">
        <div>
          <h2 className="text-xl font-semibold">Take a selfie</h2>
          <p className="mt-1 text-sm text-white/60">Face the light, look straight at the camera, no sunglasses. One clear photo is all we need.</p>
        </div>
        <div className="relative mx-auto flex aspect-square w-56 items-center justify-center overflow-hidden rounded-full border-2 border-dashed bg-white/5" style={{ borderColor: "color-mix(in srgb, var(--accent) 60%, transparent)" }}>
          {preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> : <Camera className="h-14 w-14 text-white/30" />}
          {busy && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 text-sm">
              <Loader2 className="mb-2 h-8 w-8 animate-spin text-amber-400" />
              Searching {event.photos_total?.toLocaleString("en-IN")} photos…
            </div>
          )}
        </div>
        <input ref={camRef} type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => handle(e.target.files?.[0])} />
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handle(e.target.files?.[0])} />
        <div className="grid grid-cols-2 gap-3">
          <Button disabled={busy} onClick={() => camRef.current?.click()} className="py-4"><Camera className="h-5 w-5" /> Take a selfie</Button>
          <Button disabled={busy} variant="ghost" onClick={() => fileRef.current?.click()} className="py-4"><Upload className="h-5 w-5" /> Upload a photo</Button>
        </div>
        <div className="flex items-center justify-center gap-2 text-xs text-white/50">
          <ShieldCheck className="h-4 w-4" /> Your selfie is only used to search and is never stored.
        </div>
        {onBack && <button onClick={onBack} className="mx-auto block text-sm text-white/50 underline-offset-4 hover:underline">Back</button>}
      </div>
    </>
  );
}

function Lightbox({ photos, index, onClose, onIndex, allowDownload }) {
  const p = photos[index];
  const touch = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex(Math.min(photos.length - 1, index + 1));
      if (e.key === "ArrowLeft") onIndex(Math.max(0, index - 1));
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [index, photos.length, onClose, onIndex]);
  if (!p) return null;
  return (
    <div className="fixed inset-0 z-[90] flex flex-col bg-black/95" onClick={onClose}>
      <div className="flex items-center justify-between p-3 text-sm text-white/70" onClick={(e) => e.stopPropagation()}>
        <span>{index + 1} / {photos.length}</span>
        <div className="flex items-center gap-2">
          {allowDownload && p.download_url && (
            <a href={abs(p.download_url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 hover:bg-white/20">
              <Download className="h-4 w-4" /> Full quality
            </a>
          )}
          <button onClick={onClose} className="rounded-full bg-white/10 p-2 hover:bg-white/20"><X className="h-5 w-5" /></button>
        </div>
      </div>
      <div
        className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touch.current == null) return;
          const dx = e.changedTouches[0].clientX - touch.current;
          if (dx < -40) onIndex(Math.min(photos.length - 1, index + 1));
          if (dx > 40) onIndex(Math.max(0, index - 1));
          touch.current = null;
        }}
      >
        <img src={p.preview_url || p.thumb_url} alt={p.name || ""} className="h-auto w-auto max-h-full max-w-full select-none object-contain" style={{ maxHeight: "calc(100vh - 64px)", maxWidth: "100vw" }} draggable={false} />
        {index > 0 && (
          <button onClick={() => onIndex(index - 1)} className="absolute left-2 rounded-full bg-black/50 p-2 text-white/80 hover:bg-black/70"><ChevronLeft className="h-6 w-6" /></button>
        )}
        {index < photos.length - 1 && (
          <button onClick={() => onIndex(index + 1)} className="absolute right-2 rounded-full bg-black/50 p-2 text-white/80 hover:bg-black/70"><ChevronRight className="h-6 w-6" /></button>
        )}
      </div>
    </div>
  );
}

function PhotoGrid({ photos, selectMode, selected, toggle, onOpen }) {
  return (
    <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 sm:gap-2">
      {photos.map((p, i) => {
        const on = selected.has(p.file_id);
        return (
          <button
            key={p.file_id}
            onClick={() => (selectMode ? toggle(p.file_id) : onOpen(i))}
            className={`group relative aspect-square overflow-hidden rounded-lg bg-white/5 ${on ? "ring-2 ring-amber-400" : ""}`}
          >
            <img src={p.thumb_url.replace("sz=w1000", "sz=w400")} alt="" loading="lazy" className="h-full w-full object-cover transition group-hover:scale-105" />
            {selectMode && (
              <span className={`absolute right-1.5 top-1.5 rounded-full p-0.5 ${on ? "bg-amber-400 text-black" : "bg-black/50 text-white/80"}`}>
                {on ? <Check className="h-4 w-4" /> : <Square className="h-4 w-4" />}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Results grouped by day (multi-day events). `photos` keeps its global order — `onOpen` gets the global index. */
function DayGroups({ days, photos, selectMode, selected, toggle, onOpen, canDownload, onDownloadDay, onRescan }) {
  const owner = new Map();
  days.forEach((d) => (d.file_ids || []).forEach((id) => { if (!owner.has(id)) owner.set(id, d.id); }));
  const fallback = [...days].reverse().find((d) => d.status === "uploading") || days[0];
  const groups = days.map((d) => ({ ...d, items: [] }));
  const byId = new Map(groups.map((g) => [g.id, g]));
  photos.forEach((p, i) => { (byId.get(owner.get(p.file_id)) || byId.get(fallback.id)).items.push({ p, i }); });
  const newDays = days.filter((d) => d.new_since_scan);
  return (
    <div className="space-y-6">
      {newDays.length > 0 && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
          <div className="flex-1">
            <div className="font-semibold text-amber-200">New photos since your last scan — {newDays.map((d) => d.title).join(", ")}</div>
            <div className="text-white/70">Scan again to find yourself in the newly added photos.</div>
            <Button variant="ghost" className="mt-2 px-3 py-1.5 text-xs" onClick={onRescan}><RefreshCw className="h-3.5 w-3.5" /> Scan again</Button>
          </div>
        </div>
      )}
      {groups.map((g) => (
        <div key={g.id}>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="font-semibold">{g.title}</div>
              <div className="text-xs text-white/50">{g.items.length} photo{g.items.length === 1 ? "" : "s"} of you{g.status === "uploading" ? " · more photos are being added" : ""}</div>
            </div>
            {canDownload && !selectMode && g.items.length > 0 && (
              <Button variant="ghost" className="px-3 py-1.5 text-xs" onClick={() => onDownloadDay(g, g.items.map((x) => x.p.file_id))}>
                <Download className="h-3.5 w-3.5" /> Download {g.title.split(" · ")[0]}
              </Button>
            )}
          </div>
          {g.items.length ? (
            <PhotoGrid photos={g.items.map((x) => x.p)} selectMode={selectMode} selected={selected} toggle={toggle} onOpen={(k) => onOpen(g.items[k].i)} />
          ) : (
            <div className="rounded-xl border border-dashed border-white/10 p-4 text-center text-xs text-white/40">
              {g.status === "uploading" ? "Photos from this day are still being uploaded — scan again a little later." : "None of your photos were found on this day."}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Results({ event, result, onRescan, showToast }) {
  const [tab, setTab] = useState("mine");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [lightbox, setLightbox] = useState(null);
  const [browse, setBrowse] = useState({ items: [], page: 0, pages: 1, loading: false });
  const photos = tab === "mine" ? result.photos : browse.items;
  const canDownload = event.allow_download && result.allow_download !== false;
  const shareUrl = event.share_url;
  // Day-wise grouping for multi-day events ([] = single day → flat grid). `r` lets the server flag days
  // that were added after this scan.
  const [days, setDays] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/face/public/${event.slug}/days`, { params: result?.token ? { r: result.token } : {} })
      .then((r) => alive && setDays(r.data?.grouped ? (r.data.days || []) : []))
      .catch(() => alive && setDays([]));
    return () => { alive = false; };
  }, [event.slug, result?.token]);
  const downloadDay = (g, ids) => {
    if (!canDownload || !ids.length) return;
    if (ids.length > result.zip_max_files) return showToast(`Select up to ${result.zip_max_files} photos at a time`, "info");
    downloadZip(event.slug, { token: result.token, fileIds: ids });
    showToast(`Downloading ${ids.length} photo${ids.length === 1 ? "" : "s"} from ${g.title}…`, "success");
  };

  const loadBrowse = useCallback(async (page) => {
    setBrowse((b) => ({ ...b, loading: true }));
    try {
      const pin = sessionStorage.getItem(lsKey(event.slug, "pin")) || "";
      const data = await facePublic.browse(event.slug, page, pin);
      setBrowse((b) => ({ items: page === 1 ? data.items : [...b.items, ...data.items], page, pages: data.pages, loading: false }));
    } catch (err) {
      showToast(errMsg(err), "error");
      setBrowse((b) => ({ ...b, loading: false }));
    }
  }, [event.slug, showToast]);

  useEffect(() => { if (tab === "all" && browse.page === 0) loadBrowse(1); }, [tab, browse.page, loadBrowse]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(photos.map((p) => p.file_id)));

  const downloadAll = () => {
    if (!canDownload) return;
    if (result.count > result.zip_max_files) {
      setSelectMode(true);
      return showToast(`Select up to ${result.zip_max_files} photos at a time`, "info");
    }
    downloadZip(event.slug, { token: result.token });
    showToast("Preparing your ZIP — it will start downloading shortly", "success");
  };
  const downloadSelected = () => {
    const ids = [...selected];
    if (!ids.length) return showToast("Select some photos first", "info");
    if (ids.length > result.zip_max_files) return showToast(`Max ${result.zip_max_files} photos per download`, "error");
    downloadZip(event.slug, { token: tab === "mine" ? result.token : null, fileIds: ids });
    showToast(`Downloading ${ids.length} photos…`, "success");
  };
  const copy = async (text, msg) => {
    try { await navigator.clipboard.writeText(text); showToast(msg, "success"); } catch (_) { window.prompt("Copy this link", text); }
  };
  const share = async () => {
    const text = `Find your photos from ${event.couple_names || event.title} — take a selfie here: ${shareUrl}`;
    if (navigator.share) { try { await navigator.share({ title: event.title, text, url: shareUrl }); return; } catch (_) { /* cancelled */ } }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  return (
    <>
      <Hero event={event} compact />
      <div className="mt-5">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 className="text-2xl font-semibold">We found {result.count} photo{result.count === 1 ? "" : "s"} of you <span style={{ color: "var(--accent)" }}>✨</span></h2>
            <p className="mt-1 text-sm text-white/60">Tap a photo to view it{canDownload ? " or download it in full quality" : ""}.</p>
          </div>
        </div>
        {event.allow_browse_all && (
          <div className="mt-4 flex gap-1 rounded-xl bg-white/5 p-1 text-sm">
            {[["mine", "My photos"], ["all", "Full gallery"]].map(([k, l]) => (
              <button key={k} onClick={() => { setTab(k); setSelected(new Set()); }} className={`flex-1 rounded-lg py-2 font-medium ${tab === k ? "bg-white/15" : "text-white/60"}`}>{l}</button>
            ))}
          </div>
        )}
        <div className="sticky top-0 z-20 -mx-4 mt-4 flex flex-wrap items-center gap-2 bg-[#0b0b0c]/90 px-4 py-2 backdrop-blur">
          {canDownload && !selectMode && tab === "mine" && result.count > 0 && (
            <Button onClick={downloadAll} className="px-4 py-2"><Download className="h-4 w-4" /> Download all ({result.count})</Button>
          )}
          {canDownload && (
            <Button variant="ghost" className="px-4 py-2" onClick={() => { setSelectMode(!selectMode); setSelected(new Set()); }}>
              {selectMode ? <X className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />} {selectMode ? "Cancel" : "Select"}
            </Button>
          )}
          {selectMode && (
            <>
              <Button variant="ghost" className="px-3 py-2" onClick={selectAll}>All</Button>
              <Button className="px-4 py-2" onClick={downloadSelected}><Download className="h-4 w-4" /> Download {selected.size || ""}</Button>
            </>
          )}
          {!selectMode && (
            <>
              <Button variant="ghost" className="px-4 py-2" onClick={share}><Share2 className="h-4 w-4" /> Share</Button>
              <Button variant="ghost" className="px-4 py-2" onClick={onRescan}><RefreshCw className="h-4 w-4" /> Scan again</Button>
            </>
          )}
        </div>
        <div className="mt-3">
          {tab === "mine" && result.count === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center">
              <Images className="mx-auto h-10 w-10 text-white/30" />
              <div className="mt-3 font-semibold">No matches yet</div>
              <p className="mt-1 text-sm text-white/60">Try a clearer selfie in good light, without sunglasses or a mask. If you're not in many photos, the studio may still be adding more.</p>
              <Button onClick={onRescan} className="mt-4"><Camera className="h-4 w-4" /> Try another selfie</Button>
            </div>
          ) : tab === "mine" && days.length > 1 ? (
            <DayGroups days={days} photos={photos} selectMode={selectMode} selected={selected} toggle={toggle} onOpen={setLightbox}
              canDownload={canDownload} onDownloadDay={downloadDay} onRescan={onRescan} />
          ) : (
            <PhotoGrid photos={photos} selectMode={selectMode} selected={selected} toggle={toggle} onOpen={setLightbox} />
          )}
          {tab === "all" && browse.page < browse.pages && (
            <Button variant="ghost" className="mt-4 w-full" disabled={browse.loading} onClick={() => loadBrowse(browse.page + 1)}>
              {browse.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Load more
            </Button>
          )}
        </div>
        <div className="mt-6"><StudioCard event={event} /></div>
        {result.count > 0 && tab === "mine" && (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm">
            <div className="font-semibold">Keep this link to come back to your photos</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button variant="dark" className="px-3 py-2 text-xs" onClick={() => copy(result.results_url, "Link copied")}><LinkIcon className="h-4 w-4" /> Copy my link</Button>
              <Button variant="dark" className="px-3 py-2 text-xs" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`My photos from ${event.couple_names || event.title}: ${result.results_url}`)}`, "_blank", "noopener")}><MessageCircle className="h-4 w-4" /> Send to my WhatsApp</Button>
            </div>
          </div>
        )}
      </div>
      {lightbox != null && <Lightbox photos={photos} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} allowDownload={canDownload} />}
    </>
  );
}

export default function FindMyPhotosPage() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [step, setStep] = useState("loading"); // loading | landing | guest | selfie | results | error
  const [error, setError] = useState(null);
  const [guestId, setGuestId] = useState(null);
  const [pin, setPin] = useState("");
  const [result, setResult] = useState(null);
  const [toast, showToast] = useToast();

  useEffect(() => {
    document.title = "Find your photos";
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "robots"; document.head.appendChild(meta); }
    meta.content = "noindex";
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ev = await facePublic.event(slug);
        if (cancelled) return;
        setEvent(ev);
        document.title = `${ev.couple_names || ev.title} – Find your photos`;
        const storedGuest = localStorage.getItem(lsKey(slug, "guest"));
        const storedPin = sessionStorage.getItem(lsKey(slug, "pin")) || "";
        if (storedGuest) setGuestId(storedGuest);
        if (storedPin) setPin(storedPin);
        const token = params.get("r") || localStorage.getItem(lsKey(slug, "result"));
        if (token) {
          try {
            const res = await facePublic.results(token);
            if (cancelled) return;
            setResult(res);
            setStep("results");
            return;
          } catch (_) {
            try { localStorage.removeItem(lsKey(slug, "result")); } catch (e) { /* ignore */ }
          }
        }
        setStep("landing");
      } catch (err) {
        if (cancelled) return;
        setError({ code: err?.response?.status, msg: errMsg(err, "This link doesn't seem to work.") });
        setStep("error");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const start = () => {
    if (event.require_guest_details && !guestId) return setStep("guest");
    if (event.requires_pin && !pin) return setStep("guest");
    setStep("selfie");
  };
  const onResults = (res) => {
    setResult(res);
    setStep("results");
    setParams({ r: res.token }, { replace: true });
    window.scrollTo({ top: 0 });
  };
  const rescan = () => {
    window.scrollTo({ top: 0 });
    if ((event.require_guest_details && !guestId) || (event.requires_pin && !pin)) return setStep("guest");
    setStep("selfie");
  };

  const body = useMemo(() => {
    if (step === "loading") return (
      <div className="flex min-h-[60vh] items-center justify-center text-white/60"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Opening gallery…</div>
    );
    if (step === "error") return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center text-center">
        <AlertTriangle className="h-10 w-10 text-amber-400" />
        <h1 className="mt-4 text-xl font-semibold">{error?.code === 410 ? "This gallery is no longer available" : "We couldn't open this gallery"}</h1>
        <p className="mt-2 max-w-sm text-sm text-white/60">{error?.msg}</p>
      </div>
    );
    if (step === "landing") return <Landing event={event} onStart={start} />;
    if (step === "guest") return <GuestForm event={event} showToast={showToast} onDone={(g, p) => { setGuestId(g); setPin(p || ""); setStep("selfie"); }} />;
    if (step === "selfie") return <SelfieStep event={event} guestId={guestId} pin={pin} onResults={onResults} showToast={showToast} onBack={() => setStep(result ? "results" : "landing")} />;
    if (step === "results") return <Results event={event} result={result} onRescan={rescan} showToast={showToast} />;
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, event, error, guestId, pin, result]);

  return (
    <Shell event={event}>
      <Toast toast={toast} />
      {body}
    </Shell>
  );
}
